import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Checkbox,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Tooltip,
    Typography,
    useTheme,
    Button,
} from '@material-ui/core'
import { Close, Person } from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'

import { SearchField } from '../../../components/SearchField'
import { usePagination, useUser } from '../../../hooks'
import { Company, PermissionRole, User } from '../../../models'
import {
    EDIT_PERMISSION_ROLES,
    PAYROLL_ACCESS,
    PERMISSION_TRUE,
} from '../../../models/Permissions'
import { hasPermission } from '../../../models/Users/services'
import { Container } from '../../../components'
import { BulkAddUserToWorkspacesRequest } from '../../../hooks/useOrganizationManagement'
import { CompanyTable } from './CompanyTable'
import { typographyStyle } from '../../../theme/typographyStyle'

const useLocalStyles = makeStyles((theme) => ({
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 380px)',
    },

    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
    },
    table: {
        minWidth: 750,
        tableLayout: 'fixed',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tableBody: {
        height: 'auto',
    },
    tableRow: {
        height: 48,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    emptySpace: {
        flex: 1,
        minHeight: 0,
    },
    contentContainer: {
        display: 'flex',
        flex: 1,
        gap: theme.spacing(2),
        padding: theme.spacing(1, 2),
    },

    permissionsPanel: {
        width: 250,
        padding: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        maxHeight: 'calc(100vh - 380px)',
        overflow: 'hidden',
    },

    permissionsList: {
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },

    permissionItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },

    permissionsHeader: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(1),
        marginLeft: -theme.spacing(1),
        marginRight: -theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}))

interface Props {
    userDetail: User | null
    isLoading: boolean
    organization: number | null
    companyList: Company[]
    permissionRoleList: PermissionRole[] | undefined
    onClose: () => void
    bulkAddUserToWorkspaces: (
        request: BulkAddUserToWorkspacesRequest,
    ) => Promise<void>
}

export const AddUserToWorkspaces = (props: Props) => {
    const {
        userDetail,
        isLoading,
        companyList,
        organization,
        permissionRoleList,
        onClose,
        bulkAddUserToWorkspaces,
    } = props

    const theme = useTheme()
    const localClasses = useLocalStyles()
    const { workspaceUser } = useUser()

    const roleHasPayrollAccess = (role: PermissionRole) =>
        Number(role.payroll_access) === PERMISSION_TRUE ||
        Number(role[PAYROLL_ACCESS]) === PERMISSION_TRUE

    const canAssignPayrollRoles =
        hasPermission(workspaceUser, PAYROLL_ACCESS) ||
        (workspaceUser?.permission_roles ?? []).some((userRole) => {
            const fullRole = permissionRoleList?.find(
                (r) => r.id === userRole.id,
            )
            return fullRole
                ? roleHasPayrollAccess(fullRole)
                : roleHasPayrollAccess(userRole)
        })

    const roleHasEditPermissionRolesAccess = (role: PermissionRole) =>
        Number(role.edit_permission_roles) === PERMISSION_TRUE ||
        Number(role[EDIT_PERMISSION_ROLES]) === PERMISSION_TRUE

    const canAssignEditPermissionRoles =
        hasPermission(workspaceUser, EDIT_PERMISSION_ROLES) ||
        (workspaceUser?.permission_roles ?? []).some((userRole) => {
            const fullRole = permissionRoleList?.find(
                (r) => r.id === userRole.id,
            )
            return fullRole
                ? roleHasEditPermissionRolesAccess(fullRole)
                : roleHasEditPermissionRolesAccess(userRole)
        })

    const filterAssignableRoleIds = (roleIds: number[]) => {
        if (!permissionRoleList?.length) {
            return roleIds
        }
        return roleIds.filter((id) => {
            const role = permissionRoleList.find((r) => r.id === id)
            if (!role) {
                return true
            }
            if (roleHasPayrollAccess(role) && !canAssignPayrollRoles) {
                return false
            }
            if (
                roleHasEditPermissionRolesAccess(role) &&
                !canAssignEditPermissionRoles
            ) {
                return false
            }
            return true
        })
    }

    const [searchState, setSearchState] = useState<string>('')
    const [selectedCompanies, setSelectedCompanies] = useState<
        Record<string, boolean>
    >({})
    const [selectedRoles, setSelectedRoles] = useState<Record<string, boolean>>(
        {},
    )
    const [doValidate, setDoValidate] = useState(false)

    const selectedCount = useMemo(
        () => Object.values(selectedCompanies).filter(Boolean).length,
        [selectedCompanies],
    )

    const handleCompanySelection = (companyId: string, checked: boolean) => {
        setSelectedCompanies((prev) => ({
            ...prev,
            [companyId]: checked,
        }))
    }

    const handleSelectAll = (checked: boolean) => {
        const newSelected: Record<string, boolean> = {}
        companyList.forEach((company) => {
            if (!userCompanyMap.has(company.id.toString())) {
                newSelected[company.id.toString()] = checked
            }
        })
        setSelectedCompanies(newSelected)
    }

    const handleRoleSelection = (roleId: string, checked: boolean) => {
        setSelectedRoles((prev) => ({
            ...prev,
            [roleId]: checked,
        }))
    }

    const userCompanyMap = useMemo(() => {
        if (!userDetail) return new Map<string, boolean>()
        return new Map(
            userDetail.workspaces
                .map((workspaceUser) => workspaceUser.active_workspace)
                .map((workspace) => [workspace.id.toString(), true]),
        )
    }, [userDetail])

    const filteredCompanyList = useMemo(() => {
        return companyList
            .filter((company) =>
                company.name.toLowerCase().includes(searchState.toLowerCase()),
            )
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                }),
            )
    }, [companyList, searchState])

    const userCompanyPermissionMap = useMemo(() => {
        if (!userDetail) return new Map<string, PermissionRole[]>()
        return new Map(
            userDetail.workspaces.map((workspaceUser) => [
                workspaceUser.active_workspace.id.toString(),
                workspaceUser.permission_roles.map((role) => role),
            ]),
        )
    }, [userDetail])

    const isAllSelected = useMemo(() => {
        const selectableCompanies = companyList.filter(
            (company) => !userCompanyMap.has(company.id.toString()),
        )
        return (
            selectableCompanies.length > 0 &&
            selectableCompanies.every(
                (company) => selectedCompanies[company.id.toString()],
            )
        )
    }, [companyList, selectedCompanies, userCompanyMap])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredCompanyList,
    )

    const onClickAddUserToWorkspaces = () => {
        if (!organization || !userDetail) return

        // Validate required fields
        if (Object.values(selectedRoles).every((role) => !role)) {
            setDoValidate(true)
            return
        }

        const companyIds = Object.keys(selectedCompanies)
            .filter((key) => selectedCompanies[key] === true)
            .map(Number)
        let roleIds = Object.keys(selectedRoles)
            .filter((key) => selectedRoles[key] === true)
            .map(Number)
        roleIds = filterAssignableRoleIds(roleIds)

        bulkAddUserToWorkspaces({
            body: {
                user_id: userDetail.id,
                company_ids: companyIds,
                organization_id: organization,
                permission_roles: roleIds,
            },
            params: {
                organization: organization,
            },
        }).then(() => {
            toast.success('User added to workspaces')
            onClose()
        })
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1, 2),
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                }}
            >
                <Container style={{ flexDirection: 'column' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                        }}
                    >
                        Add User to Workspaces
                    </span>
                    <Typography
                        variant="subtitle1"
                        style={{
                            marginTop: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Person fontSize="small" />
                        {userDetail?.name}
                    </Typography>
                </Container>
                <IconButton
                    onClick={onClose}
                    size="small"
                    style={{ marginLeft: 'auto' }}
                >
                    <Close />
                </IconButton>
            </Container>
            {isLoading || !userDetail ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={50} />
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Container style={{ padding: theme.spacing(1, 2) }}>
                        <SearchField
                            style={{ minWidth: 200 }}
                            placeholder="Search properties"
                            value={searchState}
                            onChange={(e) => setSearchState(e.target.value)}
                        />
                    </Container>
                    <Container className={localClasses.contentContainer}>
                        <CompanyTable
                            companies={pageData}
                            selectedCompanies={selectedCompanies}
                            onSelectCompany={handleCompanySelection}
                            onSelectAll={handleSelectAll}
                            isAllSelected={isAllSelected}
                            selectedCount={selectedCount}
                            disabledCompanies={new Set(userCompanyMap.keys())}
                            userCompanyPermissionMap={userCompanyPermissionMap}
                        />
                        <Paper className={localClasses.permissionsPanel}>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                                className={localClasses.permissionsHeader}
                            >
                                Select Permission Roles *
                            </Typography>
                            {doValidate &&
                                Object.values(selectedRoles).every(
                                    (role) => !role,
                                ) && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        style={{ marginTop: -theme.spacing(1) }}
                                    >
                                        At least one permission role is required
                                    </Typography>
                                )}
                            <div className={localClasses.permissionsList}>
                                {permissionRoleList?.map((role) => {
                                    const hasPayrollAccess = roleHasPayrollAccess(
                                        role,
                                    )
                                    const hasEditPermissionRolesAccess = roleHasEditPermissionRolesAccess(
                                        role,
                                    )
                                    const disabledRestrictedRole =
                                        (hasPayrollAccess &&
                                            !canAssignPayrollRoles) ||
                                        (hasEditPermissionRolesAccess &&
                                            !canAssignEditPermissionRoles)
                                    const restrictedRoleTooltip =
                                        hasPayrollAccess &&
                                        !canAssignPayrollRoles
                                            ? 'You need Payroll Access to assign roles with Payroll Access'
                                            : hasEditPermissionRolesAccess &&
                                              !canAssignEditPermissionRoles
                                            ? 'You need Edit Permission Roles to assign roles with Edit Permission Roles'
                                            : ''
                                    return (
                                        <div
                                            key={role.id}
                                            className={
                                                localClasses.permissionItem
                                            }
                                        >
                                            <Tooltip
                                                title={restrictedRoleTooltip}
                                                disableHoverListener={
                                                    !disabledRestrictedRole
                                                }
                                                disableFocusListener={
                                                    !disabledRestrictedRole
                                                }
                                                disableTouchListener={
                                                    !disabledRestrictedRole
                                                }
                                            >
                                                <span>
                                                    <Checkbox
                                                        checked={
                                                            selectedRoles[
                                                                role.id
                                                            ] === true
                                                        }
                                                        onChange={(e) =>
                                                            handleRoleSelection(
                                                                role.id.toString(),
                                                                e.target
                                                                    .checked,
                                                            )
                                                        }
                                                        disabled={
                                                            disabledRestrictedRole
                                                        }
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </span>
                                            </Tooltip>
                                            <Typography variant="body2">
                                                {role.name}
                                            </Typography>
                                        </div>
                                    )
                                })}
                                {(!permissionRoleList ||
                                    permissionRoleList.length === 0) && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No permission roles available
                                    </Typography>
                                )}
                            </div>
                        </Paper>
                    </Container>
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            padding: theme.spacing(1),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Pagination
                                count={numPages}
                                page={page}
                                onChange={(_: unknown, newPage: number) => {
                                    setPage(newPage)
                                }}
                            />
                            <span style={{ marginLeft: theme.spacing(2) }}>
                                {start} - {end} of {companyList?.length ?? 0}
                            </span>
                        </Container>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={selectedCount === 0}
                            onClick={onClickAddUserToWorkspaces}
                        >
                            Add to {selectedCount} workspace
                            {selectedCount !== 1 ? 's' : ''}
                        </Button>
                    </Container>
                </Container>
            )}
        </Container>
    )
}
